import { lazy, type ComponentType } from "react";

// Retry wrapper for lazy imports — handles stale cache after deploys.
//
// Bug que corrige: tras `window.location.reload()` el código seguía corriendo
// (reload es asíncrono) y llamaba `importFn()` otra vez sin `.catch()`. Ese
// segundo intento casi siempre fallaba con el mismo chunk obsoleto, y la
// promesa rechazada sin manejar tiraba el árbol completo (sin ErrorBoundary
// en ese entonces) dejando la pantalla colgada en el loader estático de
// `index.html` — el bug reportado de "se traba, hay que dar F5". Además la
// bandera nunca se limpiaba: la primera falla de CUALQUIER chunk en la
// pestaña "gastaba" el único reintento automático para el resto de la sesión.
//
// SOZU-ADMIN-1 (ticket #1439): si algo se traga el error de precarga de Vite
// (un `preventDefault` en `vite:preloadError`), el `import()` resuelve con
// `undefined` en lugar de rechazar, y `React.lazy` truena leyendo
// `undefined.default`. Un módulo sin `default` se trata igual que un chunk que
// no cargó: entra al mismo camino de reload/reintento.
export const CHUNK_RETRY_KEY = "chunk-retry";

type Modulo<T> = { default: T };

function exigirDefault<T>(mod: Modulo<T> | undefined | null): Modulo<T> {
  if (!mod || mod.default === undefined) {
    throw new Error("Módulo cargado sin export default (chunk incompleto o desfasado)");
  }
  return mod;
}

/** Lógica de carga con reintento; separada de `lazy` para poder probarla. */
export function cargarConReintento<T>(importFn: () => Promise<Modulo<T>>): Promise<Modulo<T>> {
  return importFn()
    .then((mod) => {
      const valido = exigirDefault(mod);
      // Éxito: libera el reintento para que una falla futura y no
      // relacionada también pueda disparar su propio auto-reload.
      sessionStorage.removeItem(CHUNK_RETRY_KEY);
      return valido;
    })
    .catch(() => {
      if (!sessionStorage.getItem(CHUNK_RETRY_KEY)) {
        sessionStorage.setItem(CHUNK_RETRY_KEY, "1");
        window.location.reload();
        // La página ya va de salida: no resolver ni rechazar para no
        // disparar un segundo intento (y su posible rechazo sin manejar)
        // antes de que el reload realmente ocurra.
        return new Promise<Modulo<T>>(() => {});
      }
      // Ya se intentó un reload en esta pestaña: reintentar la importación
      // una vez más (puede ser un blip transitorio de red). Si vuelve a
      // fallar, el `ErrorBoundaryGlobal` de la raíz la atrapa.
      return importFn().then(exigirDefault);
    });
}

export const lazyRetry = <T extends ComponentType<any>>(importFn: () => Promise<{ default: T }>) =>
  lazy<T>(() => cargarConReintento(importFn));
